import { useCallback, useRef } from 'react';
import type { GridApi, ColumnResizedEvent, ColumnState } from 'ag-grid-community';

/**
 * Персист ширин колонок ag-Grid у localStorage (per-grid).
 *
 * Дзеркало DataTable-персисту (sharedDataTable): власник — «коли міняю розмір
 * колонок, після оновлення вони знову старі». Для ag-Grid свій механізм ресайзу,
 * тому ловимо `onColumnResized` і зберігаємо ширини; на `onGridReady` —
 * відновлюємо через applyColumnState.
 *
 * Зберігаємо ЛИШЕ ширини (colId → width), не весь column state (порядок/сорт/
 * видимість — окрема фіча). Ключ `ag-widths:<storageKey>`.
 *
 * Використання:
 * ```tsx
 * const colW = useGridColumnWidths('contractors');
 * <AgGridReact
 *   onGridReady={(e) => { gridApiRef.current = e.api; colW.applyTo(e.api); }}
 *   onColumnResized={colW.onColumnResized}
 *   ...
 * />
 * ```
 */
const PREFIX = 'ag-widths:';

export function useGridColumnWidths(storageKey: string) {
  const lsKey = PREFIX + storageKey;
  // Захист від повторного застосування (applyTo може викликатись не раз).
  const appliedRef = useRef(false);

  // Зберегти поточні ширини (colId → width) у localStorage.
  const save = useCallback((api: GridApi) => {
    try {
      const state = api.getColumnState();
      const widths: Record<string, number> = {};
      for (const c of state) {
        if (c.colId && typeof c.width === 'number') widths[c.colId] = c.width;
      }
      localStorage.setItem(lsKey, JSON.stringify(widths));
    } catch { /* quota / приватний режим — не критично */ }
  }, [lsKey]);

  // onColumnResized — зберігати лише коли drag завершено (finished), інакше
  // спрацьовує десятки разів за один ресайз.
  const onColumnResized = useCallback((e: ColumnResizedEvent) => {
    if (e.finished && e.source === 'uiColumnResized') save(e.api);
  }, [save]);

  // applyTo(api) — викликати у onGridReady: відновити збережені ширини.
  const applyTo = useCallback((api: GridApi) => {
    if (appliedRef.current) return;
    appliedRef.current = true;
    try {
      const raw = localStorage.getItem(lsKey);
      if (!raw) return;
      const widths = JSON.parse(raw) as Record<string, number>;
      const state: Array<Pick<ColumnState, 'colId' | 'width'>> = [];
      for (const [colId, width] of Object.entries(widths)) {
        if (typeof width === 'number' && width >= 50) state.push({ colId, width });
      }
      if (state.length) {
        api.applyColumnState({ state: state as ColumnState[], applyOrder: false });
      }
    } catch { /* пошкоджений запис — ігноруємо */ }
  }, [lsKey]);

  return { onColumnResized, applyTo };
}
